import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchDisruptionRosterPage } from '@/lib/disruptions/api';
import { useCareBoard } from '@/lib/disruptions/use-disruptions';
import type { CareBoard, DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { buildCareDesk, type CareDeskState } from './care-desk-model';

// Data hook for the Care desk (feat/care-desk K5): the SAME roster cursor walk
// the Hotels desk does — deliberately under the SAME query key
// (`['disruptions', urn, 'roster', 'desk-walk']`) so the two desks share one
// cache entry and every existing invalidation (WS CaseChanged/progress pushes,
// allocate/voucher onSuccess, the 4 s denorm bridge) refreshes BOTH desks for
// free — × the K4 care board (seeded once, WS-merged by `CareChanged`). No new
// liveness wiring, no polling.

const DESK_MAX_PAGES = 20;

export interface CareDeskQuery {
  desk: CareDeskState | null;
  board: CareBoard | null;
  /** Client receive time of the board (react-query dataUpdatedAt) — the honest
   *  "Balances as of HH:MM" stamp; 0 while nothing has landed. */
  boardUpdatedAt: number;
  isLoading: boolean;
  isError: boolean;
  /** True when the roster had more pages than the walk cap — counts are a floor. */
  truncated: boolean;
  refetch: () => void;
}

export function useCareDesk(disruptionUrn: string | undefined, enabled = true): CareDeskQuery {
  const qc = useQueryClient();
  const walk = useQuery<{ rows: DisruptionPnrGroupRow[]; truncated: boolean }>({
    queryKey: ['disruptions', disruptionUrn, 'roster', 'desk-walk'],
    enabled: !!disruptionUrn && enabled,
    staleTime: 15_000,
    queryFn: async () => {
      const rows: DisruptionPnrGroupRow[] = [];
      let cursor: string | undefined;
      let truncated = false;
      for (let page = 0; page < DESK_MAX_PAGES; page += 1) {
        const res = await fetchDisruptionRosterPage(disruptionUrn!, {
          ...(cursor ? { cursor } : {}),
        });
        rows.push(...(res.items ?? []));
        cursor = res.nextCursor ?? undefined;
        if (!cursor) break;
        if (page === DESK_MAX_PAGES - 1) truncated = true;
      }
      return { rows, truncated };
    },
  });

  const board = useCareBoard(disruptionUrn, enabled);

  const desk = useMemo(
    () => (walk.data ? buildCareDesk(walk.data.rows, board.data ?? null) : null),
    [walk.data, board.data],
  );

  return {
    desk,
    board: board.data ?? null,
    boardUpdatedAt: board.dataUpdatedAt,
    // Wait for BOTH reads before classifying — a roster-only frame would flash
    // every issued group as "to issue" until the board lands.
    isLoading: walk.isLoading || board.isLoading,
    // The board is degrade-open (a failed board read still yields a roster-only
    // desk); only a failed WALK leaves nothing to stand on.
    isError: walk.isError,
    truncated: walk.data?.truncated ?? false,
    refetch: () => {
      void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'roster'] });
      void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'care'] });
    },
  };
}
